import { createBrowserHistory } from 'history';
import { isArrayLikeObject, isObjectLike, reduce } from 'lodash';
import queryString from 'query-string';

/**
 * Creation of browser history to support routing
 */
export const history = createBrowserHistory();

/**
 *
 * @param {string} url the url to check
 * @returns {boolean} true/false on whether the url is external or a path only
 */
export const isUrlExternal = (url) => /^https?:\/\//.test(url);

/**
 * Redirect to a given URL, by using window.location.assign
 * @param {string} url The url it self
 * @returns {void}
 */
export const redirectToUrl = (url) => {
  if (isUrlExternal(url)) {
    console.info('Redirect to ', url);
    window.location.assign(url);
  } else {
    history.replace(url);
  }
};

/**
 * Parses URL params
 */
export const parseURLParams = () => {
  return queryString.parse(window.location.search) ?? {};
};

/**
 * Removes the unwanted params and returns a filtered search string
 * @param {Object} params parsed params to be filtered
 * @param {Array} filterValues array with the values to be removed from the params
 * @returns {String} the new params concatenated in a search string
 */
export const getFilteredURLSearch = (params, filterValues) => {
  const newParamsArray = Object.keys(params).reduce((acc, currentKey) => {
    if (filterValues.includes(currentKey)) return acc;

    acc.push(`${currentKey}=${params[currentKey]}`);
    return acc;
  }, []);

  return newParamsArray.join('&');
};

/**
 * Wrapper over react router history.replace with added functionality
 * @param {String | Object} data The new pathname string or an object with optional pathname, search and array with params to be removed from search
 * data: { pathname: string e.g. '/path/name', search: string e.g. 'firstParam=first&secondParam=second', searchFilterValues: string[] e.g. ['access_token', 'ott'] }
 * @returns {Void}
 */
export const replaceHistory = (data) => {
  if (typeof data === 'string') {
    return history.replace(data);
  }

  const { pathname, search, searchFilterValues } = data;

  const replaceData = { pathname: pathname || window.location.pathname, search };

  if (searchFilterValues) {
    const params = parseURLParams();
    replaceData.search = getFilteredURLSearch(params, searchFilterValues);
  }

  return history.replace(replaceData);
};

/**
 * removes interpolation prefix and suffix (e.g. {{ and }} )
 * @param {string} value value to be modified (e.g. {{locals.currencySign}} )
 * @returns {string} path without interpolation prefix & suffix (e.g. locals.currencySign)
 */
export const cleanInterpolationCharacters = (value) => {
  return value.substring(2, value.length - 2);
};

/**
 * Defines formatting options per locale (language)
 * @param {Object} params Object with params
 * @param {String} params.locale The locale (language) which is currently used (e.g "en-GB")
 * @param {String} params.currency The currency coming from the config (e.g. "GBP")
 * @param {String} params.useCustomCurrencyFormat If this flag is true, the currency sign will be fetched from locals.currencySign
 * @returns {Object} Formatting options per the given locale
 */
export const defineFormattingOptions = ({ locale, currency, currencySign, useCustomCurrencyFormat }) => {
  const countryCode = new Intl.Locale(locale).region;

  const numberFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  });
  const resolvedOptions = numberFormat.resolvedOptions();
  // we need to format a number which has thousands and decimals part in order ro get their separators (comma, dot, etc)
  const parts = numberFormat.formatToParts(parseInt(1000.11)).reduce((res, part) => {
    return { ...res, [part.type]: part.value };
  }, {});

  const options = {
    locale,
    numeral: true,
    numeralPositiveOnly: true,
    maximumFractionDigits: resolvedOptions.maximumFractionDigits,
    minimumFractionDigits: resolvedOptions.minimumFractionDigits,
    numeralDecimalScale: 2,
    numeralIntegerScale: 15,
  };

  options.delimiter = parts.group || '';
  options.numeralDecimalMark = parts.decimal || '.';
  options.currencySign = useCustomCurrencyFormat && currencySign ? currencySign : parts.currency || '';
  options.countryCode = countryCode;
  options.country = countryCode;

  return options;
};

/**
 * Checking if app was opened in a webview
 * @returns {Boolean} true if webkit or appJSBridge found under window global obj or false if not
 */
export const checkIsWebView = () => {
  const { webkit, appJSBridge } = window;

  return !!webkit?.messageHandlers?.appJSBridge || !!appJSBridge;
};

/**
 * Checking if the app was opened in iOS device
 * @returns {Boolean} true if user agent contains Mac, iPhone or iPad
 */
export const checkIsIosDevice = () => {
  return /Mac|iPhone|iPad/.test(navigator.userAgent);
};

/**
 * Checking if app is opened in an iframe
 * @returns {Boolean} true if app is opened in iframe, false otherwise
 * or also true assuming we're in Iframe if accessing window.top throws a security error
 */
export const checkIsIframe = () => {
  try {
    const { self, top } = window;

    return self !== top;
  } catch (e) {
    return true;
  }
};

/**
 * Generates an RFC 4122 version 4 UUID using the platform's crypto.
 * Uses the native crypto.randomUUID when available (all modern browsers in
 * secure contexts, Node 14.17+); falls back to a getRandomValues-based
 * implementation for older browsers and jsdom (tests).
 * @returns {string} a v4 UUID, e.g. "9b2c1c1e-4f6a-4d0e-8c6b-2f9a1d3e5b7c"
 */
export const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Iterates recursively over all object fields and transforms their value by customizer.
 */
export const walkDeep = (obj, customizer) => {
  return reduce(
    obj,
    (result, value, key) => {
      if (!isObjectLike(value)) result[key] = customizer(value);
      else if (isArrayLikeObject(value)) result[key] = value.map((item) => walkDeep(item, customizer));
      else result[key] = walkDeep(value, customizer);

      return result;
    },
    {},
  );
};

/**
 * Downloads a PDF from a base64 string
 * @param {string} base64 - The base64 string of the PDF
 * @param {string} filename - The filename of the PDF
 * @returns {void}
 */
export const downloadPdfFromBase64 = (base64, filename = 'document.pdf') => {
  const clean = (base64 || '').split(',').pop();
  if (!clean) return;
  const binary = atob(clean);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'application/pdf' });

  const url = URL.createObjectURL(blob);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS) {
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
